/**
 * Maps each puzzle's `puzzleType` to the underlying concept it's teaching - shared by the
 * game header's Explain icon (opens a one-page concept guide, see puzzleConceptGuideAPI.js)
 * and its Learn icon (hands the concept off to the Coach as the topic for a new session).
 * Both features want the same "specific but generic" concept name (e.g. "Quadratic
 * Equations", not "how to focus this particular puzzle's mirror") plus a starter query
 * that primes whichever AI feature consumes it.
 *
 * `subject`/`subTopic` classify that same concept into Coach's own fixed taxonomy
 * (coachSubjects.js's COACH_SUBJECTS) - NOT sent as the session's `topic` (that stays the
 * specific concept string above, so the assessment/teaching targets it directly), but
 * needed so CoachSection.js can seed its `subject`/`subTopic` state on an autoStart
 * hand-off the same way the manual dropdown flow would. Without it, session.topic is a bare
 * concept string with no "Subject: SubTopic" shape for parseCoachTopic() to recover a
 * subject from, which left subject-driven features (the "Set treasure hunt" hand-off,
 * the "Play escape room" link) with an empty subject for any Coach session started from a
 * game's Learn icon.
 *
 * Previously lived as a local const inside GameHeader.jsx, re-created on every render and
 * only used by the Learn icon - pulled out here once the Explain icon started needing the
 * same lookup too.
 */
export const PUZZLE_CONCEPTS = {
  'ratios-liquid-mixer': {
    concept: 'Ratios & Proportions',
    query: 'Explain the concept of ratios and proportions with practical examples. How do I solve problems involving mixing liquids in different ratios?',
    subject: 'Math',
    subTopic: 'Arithmetic'
  },
  'release-pressure': {
    concept: 'Linear Equations',
    query: 'Explain linear equations and how to solve them step by step. What are the key techniques for isolating variables?',
    subject: 'Math',
    subTopic: 'Algebra'
  },
  'quadratic-lamp-focus': {
    concept: 'Quadratic Equations',
    query: 'Explain quadratic equations and parabolas with visual examples. How do parabolic reflectors work in real life?',
    subject: 'Math',
    subTopic: 'Algebra'
  },
  'rocket-launcher': {
    concept: 'Projectile Motion',
    query: 'Explain projectile motion and quadratic functions in physics. How do we calculate the trajectory of a launched object?',
    subject: 'Math',
    subTopic: 'Algebra'
  },
  'bacteria-culture-growth': {
    concept: 'Exponential Growth & Logarithms',
    query: 'Explain exponential growth equations and how logarithms are used to solve for time. How does bacteria population growth model this?',
    subject: 'Math',
    subTopic: 'Algebra'
  },
  'trigonometry-tree-height': {
    concept: 'Trigonometry (Angle of Elevation)',
    query: 'Explain how the tangent ratio and angle of elevation are used to find heights that cannot be measured directly, like the height of a tree.',
    subject: 'Math',
    subTopic: 'Geometry'
  },
  'tortoise-rabbit-race': {
    concept: 'Relative Speed & Meeting Points',
    query: 'Explain how to find when and where two objects moving at different speeds - one with a head start - meet, using distance-speed-time equations. How does this connect to solving a system of linear equations?',
    subject: 'Math',
    subTopic: 'Algebra'
  },
  'dam-outlet-flow': {
    concept: 'Derivatives & Rates of Change',
    query: 'Explain what a derivative represents as an instantaneous rate of change, using a water reservoir level rising with rainfall as an example. How do you take the derivative of linear, quadratic, logarithmic, exponential, and cubic functions?',
    subject: 'Math',
    subTopic: 'Algebra'
  },
  'reservoir-overflow-volume': {
    concept: 'Antiderivatives & Definite Integrals',
    query: "Explain what an antiderivative is and how evaluating a definite integral gives the total accumulated amount from a changing rate, like water flowing into a reservoir over a day.",
    subject: 'Math',
    subTopic: 'Algebra'
  },
  'approach-sequence': {
    concept: 'Multiplication & Division (Rate, Distance, Time)',
    query: 'Explain how distance, speed and time relate to each other, and how dividing distance by speed tells you how long something takes to arrive. How does changing the speed change that answer?',
    subject: 'Math',
    subTopic: 'Arithmetic'
  },
  'molecule-architect': {
    concept: 'Molecular Formulas & Valence',
    query: 'Explain how chemical formulas describe molecules, and how valence determines how many bonds each atom can form.',
    subject: 'Science',
    subTopic: 'Chemistry'
  },
  'molecule-builder': {
    concept: 'Molecular Formulas & Valence',
    query: 'Explain how chemical formulas describe molecules, and how valence determines how many bonds each atom can form.',
    subject: 'Science',
    subTopic: 'Chemistry'
  },
  'element-hunter': {
    concept: 'The Periodic Table',
    query: 'Explain how the periodic table is organized into groups and periods, and what those groupings tell us about an element’s properties.',
    subject: 'Science',
    subTopic: 'Chemistry'
  },
  'form-compound': {
    concept: 'Chemical Reactions & Compound Formation',
    query: 'Explain how elements combine to form compounds through a chemical reaction, like why iron and sulfur heated together form iron sulfide. What role does heat play in starting a reaction?',
    subject: 'Science',
    subTopic: 'Chemistry'
  },
  'identify-compound': {
    concept: 'Qualitative Chemical Analysis',
    query: 'Explain how scientists identify an unknown substance using properties like pH, flame test color, solubility, conductivity, and density. Why does each property give a different clue about a compound’s identity?',
    subject: 'Science',
    subTopic: 'Chemistry'
  },
  'lumber-area-fit': {
    concept: 'Area of Shapes & Composite Figures',
    query: 'Explain how to calculate the area of squares, rectangles, triangles, and regular polygons, and how smaller shapes can be combined so their areas add up to match a larger shape.',
    subject: 'Math',
    subTopic: 'Geometry'
  },
  'workbench-placeholder': {
    concept: 'Woodworking Basics',
    query: 'Explain basic woodworking measurement concepts, like why precise measuring and cutting matters when building something from lumber.',
    subject: 'Math',
    subTopic: 'Geometry'
  },
  'paint-surface-coverage': {
    concept: 'Surface Area of 2D Shapes',
    query: 'Explain how to calculate the area of squares, rectangles, triangles, circles, and composite shapes made by combining them.',
    subject: 'Math',
    subTopic: 'Geometry'
  },
  'wood-shavings-volume-fill': {
    concept: 'Volume of 3D Shapes',
    query: 'Explain how to calculate the volume of boxes, cylinders, spheres, and cones, and how combined (composite) solids add or subtract those volumes.',
    subject: 'Math',
    subTopic: 'Geometry'
  },
  'angle-cut': {
    concept: 'Angles & Angle Relationships',
    query: "Explain how angles around a point and inside shapes like rhombuses relate to each other, like why adjacent angles in a rhombus add up to 180 degrees.",
    subject: 'Math',
    subTopic: 'Geometry'
  },
  'bridge-plank-length': {
    concept: 'Geometry: Finding Missing Side Lengths',
    query: 'Explain how to find a missing side length in a shape using properties like equal sides, the Pythagorean theorem, or trigonometry.',
    subject: 'Math',
    subTopic: 'Geometry'
  },
  'map-jigsaw': {
    concept: 'World Geography: Continents & Countries',
    query: 'Explain how the continents and countries fit together on a world map, and how their shapes and neighbors help you recognize them.',
    subject: 'Social Studies',
    subTopic: 'Geography'
  },
  'chart-a-course': {
    concept: 'Map Reading & Historic Trade Routes',
    query: 'Explain how sailors historically plotted a coastal trade route port to port using a map, and how latitude and longitude describe a location.',
    subject: 'Social Studies',
    subTopic: 'Geography'
  },
  'sky-spotter': {
    concept: 'Cardinal Directions & the Night Sky',
    query: 'Explain how to find cardinal directions (N/S/E/W) by scanning the sky, and how constellations are recognized as patterns of stars.',
    subject: 'Social Studies',
    subTopic: 'Geography'
  },
  'celestial-fix': {
    concept: 'Celestial Navigation: Latitude & Longitude',
    query: "Explain how sailors used a star's height above the horizon and a chronometer to fix their ship's latitude and longitude at sea.",
    subject: 'Science',
    subTopic: 'Earth & Space Science'
  },
  'field-expedition': {
    concept: 'Topographic Maps & Compass Bearings',
    query: 'Explain how to read contour lines on a topographic map to judge slope, and how to follow a compass bearing and distance to find a location.',
    subject: 'Social Studies',
    subTopic: 'Geography'
  },
  'governors-console': {
    concept: 'Central Banking & Monetary Policy',
    query: 'Explain how a central bank uses the interest rate to manage inflation and unemployment, why raising or lowering it always helps some people and hurts others, and what other tools (like currency intervention or forward guidance) a central bank has besides the interest rate.',
    subject: 'Social Studies',
    subTopic: 'Civics & Government'
  },
  'survey-and-weigh': {
    concept: 'Volume by Cross-Section',
    query: 'Explain how to estimate the volume of an irregular solid by slicing it into cross-sections, finding each slice\'s area, and summing area times thickness - and how that connects to density, mass, and weight under different gravities.',
    subject: 'Math',
    subTopic: 'Geometry'
  },
  'tank-lift': {
    concept: 'Volume by Integration',
    query: 'Explain how to find the volume of a partially-filled tank (cylinder, cone, sphere, tapered frustum, paraboloid dish, or ellipsoid) by integrating its cross-section area as a function of height, including how to apply the power rule to find the antiderivative and evaluate a definite integral.',
    subject: 'Math',
    subTopic: 'Calculus'
  },
  'piano-keys': {
    concept: 'Major Scale Construction',
    query: 'Explain how a major scale is built from whole and half steps, and why the C major scale uses only white keys with no sharps or flats.',
    subject: 'Music',
    subTopic: 'Music Theory'
  },
  'drag-notes': {
    concept: 'Melodic Sequences & Scale Steps',
    query: 'Explain how notes in a scale relate to their neighbors, and how to figure out a missing note in a melody from the notes around it.',
    subject: 'Music',
    subTopic: 'Music Theory'
  },
  'rhythm-arrange': {
    concept: 'Note Duration & Time Signatures',
    query: 'Explain how note durations (whole, half, quarter, eighth notes) add up to fill a measure in a given time signature like 4/4.',
    subject: 'Music',
    subTopic: 'Music Theory'
  },
  'compose-melody': {
    concept: 'Scales, Key Signatures & Modulation',
    query: 'Explain how major scales and key signatures define which notes belong in a piece, and what it means for a piece to modulate to a new key partway through.',
    subject: 'Music',
    subTopic: 'Music Theory'
  },
  'chord-builder': {
    concept: 'Triads & Chord Construction',
    query: 'Explain how a major chord is built by stacking the root, third, and fifth of a scale, using C major as an example.',
    subject: 'Music',
    subTopic: 'Music Theory'
  },
  'interval-id': {
    concept: 'Musical Intervals',
    query: 'Explain how to identify the interval (distance) between two notes by counting scale steps, and how intervals like thirds and fifths sound different from each other.',
    subject: 'Music',
    subTopic: 'Music Theory'
  },
  'solve-equation': {
    concept: 'Balancing Chemical Equations',
    query: 'Explain how to balance a chemical equation so the number of atoms of each element is the same on both sides, and why that reflects conservation of mass.',
    subject: 'Science',
    subTopic: 'Chemistry'
  },
  'guess-the-person': {
    concept: 'Historical Figures',
    query: 'Help me learn about important historical figures and how to identify them from clues about their life and accomplishments.',
    subject: 'Social Studies',
    subTopic: 'World History'
  },
  'complete-the-timeline': {
    concept: 'Historical Timelines',
    query: 'Help me understand how to sequence historical events in chronological order and why that order matters.',
    subject: 'Social Studies',
    subTopic: 'World History'
  },
  'solve-the-mystery': {
    concept: 'Historical Events',
    query: 'Help me understand a major historical event - what happened, where, when, and why it mattered.',
    subject: 'Social Studies',
    subTopic: 'World History'
  },
  'archaeologists-trail': {
    concept: 'Tamil Reading Comprehension: Following a Written Clue',
    query: 'Explain how to carefully read a descriptive Tamil sentence and match it to the specific object or location it describes, including watching for a warning or negation that rules out a decoy.',
    subject: 'English',
    subTopic: 'Reading Comprehension'
  },
  'reconstruct-inscription': {
    concept: 'Tamil Sentence Structure (Word Order, Case Markers & Verb Agreement)',
    query: "Explain Tamil subject-object-verb sentence order, how the accusative (ஐ) and purposive (க்காக) case markers attach to nouns, and how the verb must agree with the subject's gender and number.",
    subject: 'English',
    subTopic: 'Grammar'
  },
  'poets-map': {
    concept: 'Sangam Literature & Thinai Imagery',
    query: 'Explain the five classical Sangam "thinai" landscapes (kurinji, neithal, mullai, marutham, paalai), the recurring imagery associated with each, and how a verse\'s images reveal its mood or theme.',
    subject: 'English',
    subTopic: 'Reading Comprehension'
  },
  'identify-the-verse': {
    concept: 'Tamil Classical Verses: Work, Author & Meaning',
    query: 'Explain how to identify which classical Tamil work (like Thirukkural or Aathichudi) a short verse comes from, who composed it, and how to work out its meaning from its words.',
    subject: 'English',
    subTopic: 'Vocabulary'
  },
  'four-inscriptions': {
    concept: 'Reading Comprehension: Cross-Referencing Multiple Clues',
    query: 'Explain how to read several short passages that each give one piece of information (who, when, their relationship, where) and combine them to figure out the full picture.',
    subject: 'English',
    subTopic: 'Reading Comprehension'
  },
  'literary-catalogue': {
    concept: 'Classifying Sangam & Classical Tamil Literature',
    query: 'Explain the major groupings of classical Tamil literature - Ettuthogai (Eight Anthologies), Pathuppattu (Ten Idylls), Pathinenkeezhkanakku (Eighteen didactic works, including Thirukkural), and the Aimperumkappiyam (Five Great Epics) - and which famous works belong to each.',
    subject: 'English',
    subTopic: 'Reading Comprehension'
  }
};

export const DEFAULT_PUZZLE_CONCEPT = {
  concept: 'Math Concepts',
  query: 'Help me understand the math concepts in this puzzle',
  subject: 'Math',
  subTopic: 'Arithmetic'
};

export function getPuzzleConcept(puzzleType) {
  return PUZZLE_CONCEPTS[puzzleType] || DEFAULT_PUZZLE_CONCEPT;
}

/**
 * History's 3 puzzle types are the one place PUZZLE_CONCEPTS' "same concept for every
 * instance" assumption breaks down: unlike "Quadratic Equations" (true regardless of which
 * numbers a given rocket-launcher instance rolls), the actual educational subject of a
 * guess-the-person/complete-the-timeline/solve-the-mystery instance genuinely differs by
 * instance - the Explain/Learn icons showing a fixed "Historical Events" for every single
 * solve-the-mystery case, whether it's about Pearl Harbor or the Boston Tea Party, meant a
 * player could never actually learn more about the specific thing they'd just solved.
 *
 * Deliberately built from data that's ALREADY visible in the puzzle's own UI regardless of
 * solve state - never the bare `answer` field - so this can safely be called before the
 * puzzle is solved without spoiling it:
 *   - guess-the-person: `category` (e.g. "Founders & Leaders") is shown under the portrait
 *     from the start; naming the category, not the person, keeps "who is this?" intact.
 *   - complete-the-timeline: the timeline's own label is already in `title` before a single
 *     card is placed (see CompleteTheTimelinePuzzle.jsx's own identical strip) - nothing here
 *     is hidden at any point, so this is always safe.
 *   - solve-the-mystery: `location`/`date` are pinned as evidence tags from the start, already
 *     a real clue toward the case - specific enough to build a good concept from without
 *     printing the `answer` text itself.
 */
const HISTORY_INSTANCE_CONCEPT_BUILDERS = {
  'guess-the-person': (puzzle) => (puzzle?.category ? {
    concept: puzzle.category,
    query: `Help me learn about important historical figures in the category of "${puzzle.category}" - who they were and why they're remembered.`,
    subject: 'Social Studies',
    subTopic: 'World History'
  } : null),
  'complete-the-timeline': (puzzle) => {
    const label = puzzle?.title?.replace('Complete the Timeline: ', '').trim();
    return label ? {
      concept: label,
      query: `Help me understand the sequence of events in "${label}" and why they happened in that order.`,
      subject: 'Social Studies',
      subTopic: 'World History'
    } : null;
  },
  'solve-the-mystery': (puzzle) => (puzzle?.location ? {
    concept: puzzle.location,
    query: `Help me understand the historical event that happened in ${puzzle.location}${puzzle.date ? ` on ${puzzle.date}` : ''} - what happened and why it mattered.`,
    subject: 'Social Studies',
    subTopic: 'World History'
  } : null)
};

// Falls back to the generic, type-level getPuzzleConcept whenever there's no puzzle data yet
// (puzzle still loading) or for every non-history puzzle type, so every other game's existing
// Explain/Learn behavior is completely unchanged.
export function getInstancePuzzleConcept(puzzleType, puzzleData) {
  const builder = HISTORY_INSTANCE_CONCEPT_BUILDERS[puzzleType];
  const instanceConcept = builder ? builder(puzzleData) : null;
  return instanceConcept || getPuzzleConcept(puzzleType);
}
